/**
 * MarketingSettingsPage.tsx
 *
 * Marketing settings — sender management, default header/footer, SMS settings, opt-out compliance.
 * Ported from account-settings.php + sms-settings.php.
 */
import React, { useState, useRef, useEffect, useMemo, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, MessageCircle, Shield, Send, Settings, Plus, Pencil, Trash2, CheckCircle, AlertTriangle, Check, Save } from 'lucide-react';
import { Tabs } from '@/components/ui/Tabs';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FormField } from '@/components/ui/FormField';
import { Spinner, SpinnerOverlay } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { apiClient } from '@/services/apiClient';
import { cn, formatPhone } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';

import type { DndEmailModel } from '@/components/dnd-editor-v2/dnd-model';
// Shared with EmailComponentsTab — resolves a stored model value (JSON object,
// or {}/null when unset) to a usable model.
import { toDndModel } from '@/components/dnd-editor-v2/dnd-saved-blocks';
import { lazyWithRetry } from '@/lib/lazyWithRetry';

const DndEditor = lazyWithRetry(() => import('@/components/dnd-editor-v2/DndEditor'), 'dnd-editor-v2');

// ─── Types ────────────────────────────────────────────────────────────────────

interface Sender {
  emailAccountSenderID: number;
  senderName: string;
  email: string;
  status: number;
  default: boolean;
  verified: boolean;
}

interface MarketingSettings {
  htmlHeader: string;
  htmlFooter: string;
  /** DnD model for the default header/footer (source of truth). `null`/absent when unset. */
  jsonHeader: DndEmailModel | null;
  jsonFooter: DndEmailModel | null;
  defaultFromNumber: string;
  /** The opt-out text in force — the default when the account has not set one. */
  optOutText: string;
  /** True when `optOutText` is the default rather than something the account saved. */
  optOutTextIsDefault: boolean;
  optOutTextDefault: string;
  optOutKeywords: string[];
  optOutMessage: string;
  optInMessage: string;
  twilioNumbers: string[];
  /** False when the Twilio lookup failed — `twilioNumbers` is then a stale DB fallback. */
  twilioLookupOk: boolean;
  verifiedEmails: string[];
  verifiedSendersWithNames: Record<string, string>;
}

interface SenderFormData {
  senderName: string;
  email: string;
  status: number;
}

// ─── API helpers ──────────────────────────────────────────────────────────────

async function fetchSettings(): Promise<MarketingSettings> {
  const r = await apiClient.get('/marketing/settings');
  return r.data.data;
}

async function saveSettings(payload: Partial<MarketingSettings>): Promise<void> {
  await apiClient.put('/marketing/settings', payload);
}

async function fetchSenders(): Promise<Sender[]> {
  const r = await apiClient.get('/marketing/settings/senders');
  const payload = r.data.data;
  return Array.isArray(payload) ? payload : [];
}

async function createSender(payload: SenderFormData): Promise<Sender> {
  const r = await apiClient.post('/marketing/settings/senders', payload);
  return r.data.data;
}

async function updateSender(id: number, payload: SenderFormData): Promise<Sender> {
  const r = await apiClient.put(`/marketing/settings/senders/${id}`, payload);
  return r.data.data;
}

async function deleteSenderApi(id: number): Promise<void> {
  await apiClient.delete(`/marketing/settings/senders/${id}`);
}

async function setDefaultSenderApi(id: number): Promise<void> {
  await apiClient.patch(`/marketing/settings/senders/${id}/default`);
}

// ─── Email Settings Tab ───────────────────────────────────────────────────────

function EmailSettingsTab() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [sendersModalOpen, setSendersModalOpen] = useState(false);
  const [senderFormOpen, setSenderFormOpen] = useState(false);
  const [editingSender, setEditingSender] = useState<Sender | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Sender | null>(null);

  // Sender form state
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formStatus, setFormStatus] = useState<number>(1);

  // Header/footer editor state
  const [editingDefaultType, setEditingDefaultType] = useState<'header' | 'footer' | null>(null);

  // Autocomplete state
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  // ── Queries ──────────────────────────────────────────────────────────────

  const settingsQuery = useQuery({
    queryKey: ['marketing', 'settings'],
    queryFn: fetchSettings,
  });

  const sendersQuery = useQuery({
    queryKey: ['marketing', 'senders'],
    queryFn: fetchSenders,
  });

  const settings = settingsQuery.data;
  const senders = sendersQuery.data ?? [];
  const defaultSender = senders.find((s) => s.default);
  const verifiedEmails = settings?.verifiedEmails ?? [];
  const verifiedSendersWithNames = settings?.verifiedSendersWithNames ?? {};

  // ── Autocomplete logic ───────────────────────────────────────────────────

  const autocompleteMatches = useMemo(() => {
    if (!formEmail.trim()) return [];
    const term = formEmail.trim().toLowerCase();
    return verifiedEmails
      .filter((em) => {
        const nm = verifiedSendersWithNames[em] ?? '';
        return em.toLowerCase().startsWith(term) || nm.toLowerCase().startsWith(term);
      })
      .map((em) => ({ email: em, name: verifiedSendersWithNames[em] ?? '' }));
  }, [formEmail, verifiedEmails, verifiedSendersWithNames]);

  const isEmailVerified = useMemo(() => {
    if (!formEmail.trim()) return null;
    return verifiedEmails.some((v) => v.toLowerCase() === formEmail.trim().toLowerCase());
  }, [formEmail, verifiedEmails]);

  function selectAutocompleteItem(email: string) {
    setFormEmail(email);
    const name = verifiedSendersWithNames[email];
    if (name) setFormName(name);
    setAutocompleteOpen(false);
    setHighlightedIndex(-1);
    emailInputRef.current?.focus();
  }

  function handleEmailKeyDown(e: React.KeyboardEvent) {
    if (!autocompleteOpen || autocompleteMatches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, autocompleteMatches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      selectAutocompleteItem(autocompleteMatches[highlightedIndex].email);
    } else if (e.key === 'Escape') {
      setAutocompleteOpen(false);
    }
  }

  // Close autocomplete on outside click
  useEffect(() => {
    if (!autocompleteOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (!autocompleteRef.current?.contains(e.target as Node)) {
        setAutocompleteOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [autocompleteOpen]);

  // ── Mutations ────────────────────────────────────────────────────────────

  const saveSenderMutation = useMutation({
    mutationFn: (data: { id?: number; payload: SenderFormData }) =>
      data.id ? updateSender(data.id, data.payload) : createSender(data.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'senders'] });
      setSenderFormOpen(false);
      setEditingSender(null);
      toast.success(editingSender ? 'Sender updated' : 'Sender added');
    },
    onError: () => {
      toast.error(editingSender ? 'Failed to update sender' : 'Failed to add sender');
    },
  });

  const deleteSenderMutation = useMutation({
    mutationFn: (id: number) => deleteSenderApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'senders'] });
      setDeleteConfirm(null);
      toast.success('Sender deleted');
    },
    onError: () => {
      toast.error('Failed to delete sender');
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: number) => setDefaultSenderApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'senders'] });
      toast.success('Default sender updated');
    },
    onError: () => {
      toast.error('Failed to set default sender');
    },
  });

  const saveHeaderFooterMutation = useMutation({
    mutationFn: (payload: Partial<MarketingSettings>) => saveSettings(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'settings'] });
      setEditingDefaultType(null);
      toast.success(`Default ${editingDefaultType} saved`);
    },
    onError: () => {
      toast.error(`Failed to save default ${editingDefaultType}`);
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  function openAddSender() {
    setEditingSender(null);
    setFormName('');
    setFormEmail('');
    setFormStatus(1);
    setAutocompleteOpen(false);
    setHighlightedIndex(-1);
    setSenderFormOpen(true);
  }

  function openEditSender(sender: Sender) {
    setEditingSender(sender);
    setFormName(sender.senderName);
    setFormEmail(sender.email);
    setFormStatus(sender.status);
    setAutocompleteOpen(false);
    setHighlightedIndex(-1);
    setSenderFormOpen(true);
  }

  function handleSaveSender() {
    const email = formEmail.trim().toLowerCase();
    // Duplicate check
    const isDuplicate = senders.some(
      (s) => s.email.toLowerCase() === email && s.emailAccountSenderID !== editingSender?.emailAccountSenderID,
    );
    if (isDuplicate) {
      toast.error('This email address is already added.');
      return;
    }
    saveSenderMutation.mutate({
      id: editingSender?.emailAccountSenderID,
      payload: { senderName: formName, email: formEmail, status: formStatus },
    });
  }

  function handleSaveHeaderFooter(model: DndEmailModel, html: string) {
    if (!editingDefaultType) return;
    const payload: Partial<MarketingSettings> = editingDefaultType === 'header'
      ? { jsonHeader: model, htmlHeader: html }
      : { jsonFooter: model, htmlFooter: html };
    saveHeaderFooterMutation.mutate(payload);
  }

  // ── Render: Loading ──────────────────────────────────────────────────────

  if (settingsQuery.isLoading || sendersQuery.isLoading) {
    return <SpinnerOverlay label="Loading email settings..." />;
  }

  // ── Render ───────────────────────────────────────────────────────────────

  const currentHeader = settings?.htmlHeader ?? '';
  const currentFooter = settings?.htmlFooter ?? '';

  return (
    <div className="space-y-6 max-w-[900px] p-5">
      {/* ── Default Sender Section ──────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 text-base font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-200">
          <Mail className="h-4 w-4 text-sky-700" />
          Default Sender
        </div>

        <Card className="flex items-center justify-between">
          {defaultSender ? (
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                Default sender for campaigns
              </p>
              <p className="font-semibold text-sm text-slate-800">{defaultSender.senderName}</p>
              <p className="text-xs text-slate-500 mt-0.5">{defaultSender.email}</p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No default sender configured</p>
          )}
          <Button variant="secondary" size="sm" onClick={() => setSendersModalOpen(true)}>
            Manage Senders
          </Button>
        </Card>
      </div>

      {/* ── Email Defaults Section ──────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 text-base font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-200">
          <Settings className="h-4 w-4 text-sky-700" />
          Email Defaults
        </div>

        {/* Default Header Card */}
        <Card noPadding className="mb-4 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 bg-slate-50 border-b border-slate-200">
            <div>
              <p className="font-semibold text-sm text-slate-800">Default Header</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Used by the Header block in the email builder
              </p>
            </div>
            <Button size="sm" onClick={() => setEditingDefaultType('header')}>
              Edit
            </Button>
          </div>
          <div className="h-40 bg-slate-50 relative">
            {currentHeader ? (
              <iframe
                srcDoc={currentHeader}
                sandbox="allow-same-origin"
                className="w-full h-full border-none"
                title="Header preview"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-slate-300 italic">
                No default header set
              </div>
            )}
          </div>
        </Card>

        {/* Default Footer Card */}
        <Card noPadding className="overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 bg-slate-50 border-b border-slate-200">
            <div>
              <p className="font-semibold text-sm text-slate-800">Default Footer</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Used by the Footer block in the email builder
              </p>
            </div>
            <Button size="sm" onClick={() => setEditingDefaultType('footer')}>
              Edit
            </Button>
          </div>
          <div className="h-40 bg-slate-50 relative">
            {currentFooter ? (
              <iframe
                srcDoc={currentFooter}
                sandbox="allow-same-origin"
                className="w-full h-full border-none"
                title="Footer preview"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-slate-300 italic">
                No default footer set
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* ── Senders List Modal ──────────────────────────────────── */}
      <Modal
        isOpen={sendersModalOpen}
        onClose={() => setSendersModalOpen(false)}
        title="Sender Emails"
        size="lg"
        footer={
          <div className="flex items-center gap-2 w-full justify-between">
            <Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={openAddSender}>
              Add Sender
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setSendersModalOpen(false)}>
              Close
            </Button>
          </div>
        }
      >
        {sendersQuery.isLoading ? (
          <SpinnerOverlay label="Loading senders..." />
        ) : senders.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-slate-400 mb-3">No sender emails configured yet.</p>
            <Button size="sm" onClick={openAddSender}>
              Add Your First Sender
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {senders.map((sender) => (
              <div
                key={sender.emailAccountSenderID}
                className="flex items-center justify-between rounded-lg border border-slate-200 p-3 hover:shadow-sm transition-shadow"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800">{sender.senderName}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{sender.email}</p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    {sender.default && (
                      <Badge variant="info" size="sm">Default</Badge>
                    )}
                    <Badge variant={sender.status === 1 ? 'success' : 'error'} size="sm">
                      {sender.status === 1 ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge variant={sender.verified ? 'success' : 'warning'} size="sm">
                      {sender.verified ? 'Verified' : 'Not Verified'}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 ml-4 flex-shrink-0">
                  {sender.default ? (
                    <Button variant="primary" size="xs" disabled leftIcon={<Check className="h-3 w-3" />}>
                      Default
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => setDefaultMutation.mutate(sender.emailAccountSenderID)}
                      loading={setDefaultMutation.isPending}
                    >
                      Set Default
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="xs"
                    iconOnly
                    onClick={() => openEditSender(sender)}
                    aria-label="Edit sender"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    iconOnly
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => setDeleteConfirm(sender)}
                    aria-label="Delete sender"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* ── Sender Form Modal (Add / Edit) ──────────────────────── */}
      <Modal
        isOpen={senderFormOpen}
        onClose={() => { setSenderFormOpen(false); setEditingSender(null); }}
        title={editingSender ? 'Edit Sender Email' : 'Add Sender Email'}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setSenderFormOpen(false); setEditingSender(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveSender}
              loading={saveSenderMutation.isPending}
              disabled={!formName.trim() || !formEmail.trim()}
            >
              {editingSender ? 'Save Changes' : 'Add Sender'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Sender Name */}
          <FormField label="Sender Name" htmlFor="sender-name" required>
            <Input
              id="sender-name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g. Dr. Smith's Office"
            />
          </FormField>

          {/* Sender Email with autocomplete */}
          <FormField label="Sender Email" htmlFor="sender-email" required>
            <div className="relative" ref={autocompleteRef}>
              <Input
                ref={emailInputRef}
                id="sender-email"
                type="email"
                value={formEmail}
                onChange={(e) => {
                  setFormEmail(e.target.value);
                  setAutocompleteOpen(true);
                  setHighlightedIndex(-1);
                }}
                onFocus={() => {
                  if (formEmail.trim()) setAutocompleteOpen(true);
                }}
                onKeyDown={handleEmailKeyDown}
                placeholder="e.g. office@yourpractice.com"
                autoComplete="off"
              />

              {/* Autocomplete dropdown */}
              {autocompleteOpen && autocompleteMatches.length > 0 && (
                <div className="absolute z-[100] top-full left-0 right-0 bg-white border border-slate-200 border-t-0 rounded-b-md shadow-lg max-h-[200px] overflow-y-auto">
                  {autocompleteMatches.map((match, i) => (
                    <div
                      key={match.email}
                      className={cn(
                        'flex items-center justify-between px-3 py-2 cursor-pointer text-sm border-b border-slate-50 last:border-b-0',
                        i === highlightedIndex ? 'bg-sky-50' : 'hover:bg-sky-50',
                      )}
                      onMouseDown={() => selectAutocompleteItem(match.email)}
                    >
                      <div>
                        <span className="font-medium text-slate-800">{match.email}</span>
                        {match.name && (
                          <span className="block text-xs text-slate-400">{match.name}</span>
                        )}
                      </div>
                      <span className="text-xs font-semibold text-green-600 whitespace-nowrap">
                        <CheckCircle className="h-3 w-3 inline mr-0.5" />
                        Verified
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Verification status indicator */}
            {formEmail.trim() && isEmailVerified !== null && (
              <div
                className={cn(
                  'flex items-center gap-1.5 mt-1.5 px-2.5 py-1.5 rounded text-xs font-medium',
                  isEmailVerified
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-yellow-50 text-yellow-700 border border-yellow-300',
                )}
              >
                {isEmailVerified ? (
                  <>
                    <CheckCircle className="h-3.5 w-3.5" />
                    Verified SendGrid sender
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Not a verified SendGrid sender
                  </>
                )}
              </div>
            )}
          </FormField>

          {/* Status */}
          <FormField label="Status" htmlFor="sender-status">
            <Select
              id="sender-status"
              value={formStatus}
              onChange={(val) => setFormStatus(val ?? 1)}
              options={[
                { value: 1, label: 'Active' },
                { value: 0, label: 'Inactive' },
              ]}
            />
          </FormField>
        </div>
      </Modal>

      {/* ── Delete Confirmation ─────────────────────────────────── */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm) return deleteSenderMutation.mutateAsync(deleteConfirm.emailAccountSenderID);
        }}
        title="Delete Sender"
        message={`Are you sure you want to delete "${deleteConfirm?.email}"?`}
        confirmLabel="Delete"
        variant="danger"
      />

      {/* ── Header/Footer Editor (Full-screen, portaled to body) ── */}
      {/* The DnD editor owns its own toolbar (Save + exit-fullscreen → onClose). We
          intentionally do NOT pass `savedBlocks` here so the Header/Footer palette
          blocks are not offered while editing the header/footer itself. */}
      {editingDefaultType && createPortal(
        <div className="fixed inset-0 bg-slate-100 z-[9999]">
          <Suspense fallback={<div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>}>
            <DndEditor
              key={editingDefaultType}
              initialModel={toDndModel(editingDefaultType === 'header' ? settings?.jsonHeader : settings?.jsonFooter)}
              initialFullscreen
              onSave={handleSaveHeaderFooter}
              onClose={() => setEditingDefaultType(null)}
            />
          </Suspense>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ─── SMS Settings Tab ─────────────────────────────────────────────────────────

function SmsSettingsTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const userLevel = useAuthStore((s) => s.user?.userLevel ?? 0);
  const canEditFromNumber = userLevel >= 5;

  const [defaultFromNumber, setDefaultFromNumber] = useState('');
  const [optOutText, setOptOutText] = useState('');
  const [optOutKeywords, setOptOutKeywords] = useState('');
  const [optOutMessage, setOptOutMessage] = useState('');
  const [optInMessage, setOptInMessage] = useState('');
  const [initialized, setInitialized] = useState(false);

  const settingsQuery = useQuery({
    queryKey: ['marketing', 'settings'],
    queryFn: fetchSettings,
  });

  const settings = settingsQuery.data;

  const normalizedTwilioNumbers = useMemo(() => {
    if (!settings?.twilioNumbers) return [];
    return settings.twilioNumbers.map((n) => {
      const d = n.replace(/\D/g, '');
      // Compare on the national 10-digit form regardless of country code / formatting.
      return d.length >= 10 ? d.slice(-10) : d;
    });
  }, [settings?.twilioNumbers]);

  // 'unverified' = we couldn't ask Twilio, so we can't claim the number is bad.
  const phoneValidation = useMemo<'valid' | 'partial' | 'invalid' | 'unverified' | null>(() => {
    if (!defaultFromNumber) return null;
    if (defaultFromNumber.length < 10) return 'partial';
    if (defaultFromNumber.length === 10) {
      if (normalizedTwilioNumbers.includes(defaultFromNumber)) return 'valid';
      return settings?.twilioLookupOk === false ? 'unverified' : 'invalid';
    }
    return null;
  }, [defaultFromNumber, normalizedTwilioNumbers, settings?.twilioLookupOk]);

  useEffect(() => {
    if (settings && !initialized) {
      const raw = (settings.defaultFromNumber ?? '').replace(/\D/g, '');
      setDefaultFromNumber(raw.length >= 10 ? raw.slice(-10) : raw);
      setOptOutText(settings.optOutText ?? '');
      setOptOutKeywords(
        Array.isArray(settings.optOutKeywords) ? settings.optOutKeywords.join(', ') : '',
      );
      setOptOutMessage(settings.optOutMessage ?? '');
      setOptInMessage(settings.optInMessage ?? '');
      setInitialized(true);
    }
  }, [settings, initialized]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        optOutText,
        optOutKeywords: optOutKeywords
          .split(',')
          .map((k) => k.trim())
          .filter(Boolean),
        optOutMessage,
        optInMessage,
      };
      if (canEditFromNumber) {
        payload.defaultFromNumber = defaultFromNumber.replace(/\D/g, '');
      }
      return saveSettings(payload as Partial<MarketingSettings>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'settings'] });
      // The API stores the default in place of a blank, so show what it kept.
      if (!optOutText.trim() && settings?.optOutTextDefault) setOptOutText(settings.optOutTextDefault);
      toast.success('SMS settings saved successfully');
    },
    onError: () => {
      toast.error('Failed to save SMS settings');
    },
  });

  if (settingsQuery.isLoading) {
    return <SpinnerOverlay label="Loading SMS settings..." />;
  }

  return (
    <div className="space-y-5 max-w-[700px] p-5">
      {/* ── Sending Configuration ───────────────────────────────── */}
      <Card>
        <div className="flex items-center gap-2 text-[15px] font-semibold text-slate-800 mb-4 pb-3 border-b border-slate-100">
          <Send className="h-[18px] w-[18px] text-emerald-700" />
          Sending Configuration
        </div>

        <FormField label="Marketing SMS From Number" htmlFor="sms-marketing-number">
          {canEditFromNumber ? (
            <>
              <Input
                id="sms-marketing-number"
                value={
                  defaultFromNumber.length === 10
                    ? `(${defaultFromNumber.slice(0, 3)}) ${defaultFromNumber.slice(3, 6)}-${defaultFromNumber.slice(6)}`
                    : defaultFromNumber
                }
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setDefaultFromNumber(digits);
                }}
                placeholder="e.g. (555) 123-4567"
              />
              {phoneValidation && (
                <div
                  className={cn(
                    'flex items-center gap-1.5 mt-1.5 px-2.5 py-1.5 rounded text-xs font-medium',
                    phoneValidation === 'valid' && 'bg-green-50 text-green-700 border border-green-200',
                    phoneValidation === 'partial' && 'bg-amber-50 text-amber-700 border border-amber-200',
                    phoneValidation === 'unverified' && 'bg-amber-50 text-amber-700 border border-amber-200',
                    phoneValidation === 'invalid' && 'bg-red-50 text-red-700 border border-red-200',
                  )}
                >
                  {phoneValidation === 'valid' && (
                    <><CheckCircle className="h-3.5 w-3.5" />Valid Twilio number</>
                  )}
                  {phoneValidation === 'partial' && (
                    <><AlertTriangle className="h-3.5 w-3.5" />{10 - defaultFromNumber.length} digits remaining</>
                  )}
                  {phoneValidation === 'unverified' && (
                    <><AlertTriangle className="h-3.5 w-3.5" />Couldn't reach Twilio to verify this number</>
                  )}
                  {phoneValidation === 'invalid' && (
                    <><AlertTriangle className="h-3.5 w-3.5" />Not a registered Twilio number on this account</>
                  )}
                </div>
              )}
            </>
          ) : (
            <Input
              id="sms-marketing-number"
              value={settings?.defaultFromNumber ? formatPhone(settings.defaultFromNumber) : ''}
              disabled
              className="bg-slate-50 text-slate-500 cursor-not-allowed"
            />
          )}
          <p className="text-xs text-slate-400 mt-1">
            All marketing campaigns and automations send from this number. STOP replies to this number only unsubscribe from marketing SMS.
          </p>
        </FormField>
      </Card>

      {/* ── Opt-Out / Compliance ─────────────────────────────────── */}
      <Card>
        <div className="flex items-center gap-2 text-[15px] font-semibold text-slate-800 mb-4 pb-3 border-b border-slate-100">
          <Shield className="h-[18px] w-[18px] text-emerald-700" />
          Opt-Out / Compliance
        </div>

        <div className="space-y-4">
          <FormField
            label="Opt-Out Instruction Text"
            htmlFor="sms-opt-out-text"
            helpText={`This text replaces the {{opt_out}} variable in your outgoing messages. Every campaign and automation message carries it. Leave it blank to use the default${settings?.optOutTextDefault ? `: "${settings.optOutTextDefault}"` : ''}.`}
          >
            <div className="mb-1">
              <span className="text-xs text-slate-400">
                (used by <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">{`{{opt_out}}`}</code> variable)
              </span>
            </div>
            <Textarea
              id="sms-opt-out-text"
              value={optOutText}
              onChange={(e) => setOptOutText(e.target.value)}
              placeholder="e.g. Reply STOP to opt out."
              rows={2}
            />
          </FormField>

          <FormField
            label="Opt-Out Keywords"
            htmlFor="sms-opt-out-keywords"
            helpText="Comma-separated keywords that trigger opt-out. Twilio automatically handles STOP, STOPALL, UNSUBSCRIBE, CANCEL, END, and QUIT."
          >
            <Input
              id="sms-opt-out-keywords"
              value={optOutKeywords}
              onChange={(e) => setOptOutKeywords(e.target.value)}
              placeholder="STOP, UNSUBSCRIBE, CANCEL, END, QUIT"
            />
          </FormField>

          <FormField
            label="Opt-Out Auto-Reply Message"
            htmlFor="sms-opt-out-message"
            helpText="Sent back to the recipient when they reply with an opt-out keyword."
          >
            <Textarea
              id="sms-opt-out-message"
              value={optOutMessage}
              onChange={(e) => setOptOutMessage(e.target.value)}
              placeholder="Auto-reply sent when someone texts STOP"
              rows={3}
            />
          </FormField>

          <FormField
            label="Opt-In Auto-Reply Message"
            htmlFor="sms-opt-in-message"
            helpText="Sent back to the recipient when they reply with START to resubscribe."
          >
            <Textarea
              id="sms-opt-in-message"
              value={optInMessage}
              onChange={(e) => setOptInMessage(e.target.value)}
              placeholder="Auto-reply sent when someone texts START"
              rows={3}
            />
          </FormField>
        </div>
      </Card>

      {/* Only a definite verdict blocks saving — 'unverified' means Twilio was
          unreachable, which must not lock the user out of their own settings. */}
      <Button
        onClick={() => saveMutation.mutate()}
        loading={saveMutation.isPending}
        disabled={canEditFromNumber && (phoneValidation === 'invalid' || phoneValidation === 'partial')}
      >
        Save SMS Settings
      </Button>
    </div>
  );
}

// ─── Variable Defaults Tab ────────────────────────────────────────────────────

const VARIABLE_GROUPS = [
  {
    label: 'Recipient',
    vars: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
    ],
  },
  {
    label: 'Facility Info',
    vars: [
      { key: 'facility_name', label: 'Facility Name' },
      { key: 'facility_address', label: 'Address' },
      { key: 'facility_city', label: 'City' },
      { key: 'facility_state', label: 'State' },
      { key: 'facility_zip', label: 'Zip / Postal Code' },
      { key: 'facility_country', label: 'Country' },
      { key: 'facility_phone', label: 'Phone' },
      { key: 'facility_email', label: 'Email' },
    ],
  },
  {
    label: 'Facility Links',
    vars: [
      { key: 'facility_website', label: 'Website' },
      { key: 'facility_facebook', label: 'Facebook' },
      { key: 'facility_twitter', label: 'Twitter / X' },
      { key: 'facility_instagram', label: 'Instagram' },
      { key: 'facility_pinterest', label: 'Pinterest' },
      { key: 'facility_linkedin', label: 'LinkedIn' },
      { key: 'facility_tiktok', label: 'TikTok' },
      { key: 'facility_youtube', label: 'YouTube' },
    ],
  },
];

function VariableDefaultsTab() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ['marketing', 'settings'],
    queryFn: fetchSettings,
  });

  const [defaults, setDefaults] = useState<Record<string, string>>({});
  const [initialized, setInitialized] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (settingsQuery.data && !initialized) {
      const vd = (settingsQuery.data as any).variableDefaults;
      if (vd && typeof vd === 'object') {
        setDefaults(vd);
      }
      setInitialized(true);
    }
  }, [settingsQuery.data, initialized]);

  const handleChange = (key: string, value: string) => {
    setDefaults((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const saveMutation = useMutation({
    mutationFn: () => saveSettings({ variableDefaults: defaults } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'settings'] });
      setDirty(false);
      toast.success('Variable defaults saved');
    },
    onError: () => {
      toast.error('Failed to save variable defaults');
    },
  });

  if (settingsQuery.isLoading) {
    return <div className="flex justify-center py-12"><Spinner /></div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <Card>
        <CardHeader>
          <h3 className="text-base font-semibold text-slate-900">Global Variable Defaults</h3>
          <p className="text-sm text-slate-500 mt-1">
            Set fallback values for variables when recipient or facility data is missing.
            These apply to all email and SMS campaigns unless overridden with an inline default
            (e.g. <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">{'{{first_name [[Friend]]}}'}</code>).
          </p>
        </CardHeader>
        <CardBody className="space-y-6">
          {VARIABLE_GROUPS.map((group) => (
            <div key={group.label}>
              <h4 className="text-sm font-medium text-slate-700 mb-3">{group.label}</h4>
              <div className="space-y-2">
                {group.vars.map((v) => (
                  <div key={v.key} className="flex items-center gap-3">
                    <code className="text-xs text-sky-600 bg-slate-50 px-2 py-1 rounded w-48 shrink-0 font-mono">
                      {`{{${v.key}}}`}
                    </code>
                    <Input
                      value={defaults[v.key] || ''}
                      onChange={(e) => handleChange(v.key, e.target.value)}
                      placeholder={`Default ${v.label.toLowerCase()}...`}
                      className="h-8 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="flex justify-end pt-2">
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={!dirty || saveMutation.isPending}
              size="sm"
            >
              {saveMutation.isPending ? <Spinner className="h-4 w-4 mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save Defaults
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MarketingSettingsPage() {
  const tabs = [
    {
      id: 'email',
      label: 'Email Settings',
      icon: <Mail className="h-4 w-4" />,
      content: <EmailSettingsTab />,
    },
    {
      id: 'sms',
      label: 'SMS Settings',
      icon: <MessageCircle className="h-4 w-4" />,
      content: <SmsSettingsTab />,
    },
    {
      id: 'variables',
      label: 'Variables',
      icon: <Settings className="h-4 w-4" />,
      content: <VariableDefaultsTab />,
    },
  ];

  return (
    <div className="pt-2">
      <Tabs tabs={tabs} defaultTab="email" />
    </div>
  );
}
